import React from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

const ProblemSection = styled.section`
  padding: 120px 0;
  background: linear-gradient(180deg, ${props => props.theme.colors.dark} 0%, ${props => props.theme.colors.darkGray} 100%);
  position: relative;

  @keyframes iconFloat {
    0%, 100% { transform: translateY(0px); }
    50% { transform: translateY(-5px); }
  }
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 80px;
`;

const Title = styled(motion.h2)`
  font-family: ${props => props.theme.fonts.display};
  font-size: clamp(2rem, 4vw, 3.5rem);
  font-weight: 700;
  margin-bottom: 24px;
  color: ${props => props.theme.colors.white};
`;

const Subtitle = styled(motion.p)`
  font-size: 1.25rem;
  color: ${props => props.theme.colors.lightGray};
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.6;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1rem;
  }
`;

const ProblemsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const ProblemCard = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.mediumGray}40, ${props => props.theme.colors.darkGray}60);
  border: 1px solid ${props => props.theme.colors.mediumGray};
  border-radius: 16px;
  padding: 40px;
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 24px;
    border-radius: 12px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.accent}, ${props => props.theme.colors.primary});
  }
`;

const ProblemIcon = styled(motion.div)`
  width: 60px;
  height: 60px;
  border-radius: 12px;
  background: linear-gradient(135deg, ${props => props.theme.colors.accent}20, ${props => props.theme.colors.primary}20);
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;
  font-size: 24px;
  color: ${props => props.theme.colors.accent};
  transition: all 0.3s ease;
`;

const ProblemTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 600;
  color: ${props => props.theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.25rem;
  }
`;

const ProblemDescription = styled.p`
  color: ${props => props.theme.colors.lightGray};
  line-height: 1.6;
  margin-bottom: 20px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
  }
`;

const ProblemImpact = styled.div`
  background: ${props => props.theme.colors.accent}10;
  border: 1px solid ${props => props.theme.colors.accent}30;
  border-radius: 8px;
  padding: 16px;
  color: ${props => props.theme.colors.accent};
  font-weight: 500;
  font-size: 0.9rem;
`;

const CostCalculator = styled(motion.div)`
  background: linear-gradient(135deg, ${props => props.theme.colors.darkGray}, ${props => props.theme.colors.mediumGray});
  border-radius: 20px;
  padding: 60px;
  text-align: center;
  position: relative;
  overflow: hidden;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    padding: 30px 20px;
    border-radius: 16px;
  }

  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(45deg, ${props => props.theme.colors.accent}05, ${props => props.theme.colors.primary}05);
    z-index: 1;
  }

  > * {
    position: relative;
    z-index: 2;
  }
`;

const CostTitle = styled.h3`
  font-family: ${props => props.theme.fonts.display};
  font-size: 2.5rem;
  font-weight: 700;
  color: ${props => props.theme.colors.white};
  margin-bottom: 16px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 1.8rem;
  }
`;

const CostDescription = styled.p`
  font-size: 1.1rem;
  color: ${props => props.theme.colors.lightGray};
  margin-bottom: 40px;
  max-width: 500px;
  margin-left: auto;
  margin-right: auto;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 0.95rem;
    margin-bottom: 30px;
  }
`;

const CostStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
  gap: 40px;
  
  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    grid-template-columns: 1fr;
    gap: 24px;
  }
`;

const CostStat = styled.div`
  text-align: center;
`;

const CostNumber = styled(motion.div)`
  font-family: ${props => props.theme.fonts.display};
  font-size: 3rem;
  font-weight: 800;
  color: ${props => props.theme.colors.accent};
  line-height: 1;
  margin-bottom: 8px;

  @media (max-width: ${props => props.theme.breakpoints.mobile}) {
    font-size: 2.2rem;
  }
`;

const CostLabel = styled.div`
  color: ${props => props.theme.colors.lightGray};
  font-size: 1rem;
`;

const Problem = () => {
  const [ref, inView] = useInView({
    triggerOnce: false,
    threshold: 0.1
  });



  const problems = [
    {
      icon: "!",
      title: "Estoque Parado Custando Caro",
      description: "Veículos ocupando espaço físico e gerando custos mensais sem retorno financeiro adequado.",
      impact: "Prejuízo constante com veículos parados"
    },
    {
      icon: "↓",
      title: "Baixo Giro de Estoque",
      description: "Dificuldade para vender veículos rapidamente, resultando em capital imobilizado por longos períodos.",
      impact: "Capital parado sem gerar retorno"
    },
    {
      icon: "%",
      title: "Margens de Lucro Reduzidas",
      description: "Competição acirrada forçando redução de preços e comprometendo a rentabilidade do negócio.",
      impact: "Lucro comprometido pela concorrência"
    },
    {
      icon: "X",
      title: "Falta de Estratégia de Vendas",
      description: "Ausência de metodologia estruturada para atrair clientes e fechar negócios de forma consistente.",
      impact: "Vendas inconsistentes e imprevisíveis"
    }
  ];

  return (
    <ProblemSection ref={ref}>
      <Container>
        <Header>
          <Title
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
          >
            VOCÊ ENFRENTA ESSES DESAFIOS?
          </Title>
          <Subtitle
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            Conheça os principais obstáculos que impedem lojistas de alcançar todo seu potencial de lucro
          </Subtitle>
        </Header>

        <ProblemsGrid>
          {problems.map((problem, index) => (
            <ProblemCard
              key={index}
              initial={{ opacity: 0, y: 50, scale: 0.9 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              whileHover={{ y: -5, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              <ProblemIcon
                whileHover={{ scale: 1.1, rotate: 5 }}
                animate={inView ? {
                  y: [0, -3, 0],
                  transition: {
                    duration: 2,
                    repeat: Infinity,
                    delay: index * 0.3
                  }
                } : {}}
              >
                {problem.icon}
              </ProblemIcon>
              <ProblemTitle>{problem.title}</ProblemTitle>
              <ProblemDescription>{problem.description}</ProblemDescription>
              <ProblemImpact>{problem.impact}</ProblemImpact>
            </ProblemCard>
          ))}
        </ProblemsGrid>


      </Container>
    </ProblemSection>
  );
};

export default Problem;